import { useEffect, useMemo, useReducer } from "react";
import Decimal from "decimal.js";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import {
  type OtcInvoiceQueueAllocationProjection,
  type OtcInvoiceQueueListItem,
  useUpdateOtcInvoiceAllocations,
} from "@/lib/api/otcInvoices";
import { UpdateOtcInvoiceAllocationsRequestValidation } from "@/lib/validation/otcInvoices";
import { getAmendmentTypeDisplay } from "@/section/otc/contract-form-v3/amendmentTypes";

type CustomerOption = { value: string; label: string };

type AllocationDraft = {
  key: string;
  id?: string;
  customerId: string;
  percentage: string;
};

type AllocationDialogState = {
  allocations: AllocationDraft[];
  nextKey: number;
  isSubmitting: boolean;
  error: string | null;
};

type AllocationDialogAction =
  | { type: "initialize"; allocations: OtcInvoiceQueueAllocationProjection[] }
  | { type: "add" }
  | { type: "remove"; key: string }
  | { type: "set_customer"; key: string; customerId: string }
  | { type: "set_percentage"; key: string; percentage: string }
  | { type: "submit_started" }
  | { type: "submit_failed"; error: string }
  | { type: "submit_finished" };

const initialState: AllocationDialogState = {
  allocations: [],
  nextKey: 1,
  isSubmitting: false,
  error: null,
};

function validPercentage(value: string): Decimal | null {
  try {
    const percentage = new Decimal(value);
    return percentage.isFinite() && percentage.isPositive() ? percentage : null;
  } catch {
    return null;
  }
}

function addAllocation(allocations: AllocationDraft[], nextKey: number): AllocationDraft[] {
  const newAllocation = { key: `new-${nextKey}`, customerId: "", percentage: "100" };
  const firstAllocation = allocations[0];
  if (!firstAllocation) return [newAllocation];

  const firstPercentage = validPercentage(firstAllocation.percentage);
  const splitPercentage = firstPercentage
    ? firstPercentage.div(2)
    : allocations.slice(1).reduce(
      (remaining: Decimal, allocation: AllocationDraft): Decimal => {
        const percentage = validPercentage(allocation.percentage);
        return percentage ? remaining.minus(percentage) : remaining;
      },
      new Decimal(100),
    ).clamp(0, 100).div(2);
  const percentage = splitPercentage.toString();

  return [
    { ...firstAllocation, percentage },
    ...allocations.slice(1),
    { ...newAllocation, percentage },
  ];
}

function allocationReducer(state: AllocationDialogState, action: AllocationDialogAction): AllocationDialogState {
  switch (action.type) {
    case "initialize":
      return {
        allocations: action.allocations.map(
          (allocation: OtcInvoiceQueueAllocationProjection): AllocationDraft => ({
            key: allocation.id,
            id: allocation.id,
            customerId: allocation.customer_id,
            percentage: allocation.percentage,
          }),
        ),
        nextKey: 1,
        isSubmitting: false,
        error: null,
      };
    case "add":
      return {
        ...state,
        allocations: addAllocation(state.allocations, state.nextKey),
        nextKey: state.nextKey + 1,
        error: null,
      };
    case "remove":
      return { ...state, allocations: state.allocations.filter((allocation: AllocationDraft): boolean => allocation.key !== action.key), error: null };
    case "set_customer":
      return {
        ...state,
        allocations: state.allocations.map((allocation: AllocationDraft): AllocationDraft => allocation.key === action.key ? { ...allocation, customerId: action.customerId } : allocation),
        error: null,
      };
    case "set_percentage":
      return {
        ...state,
        allocations: state.allocations.map((allocation: AllocationDraft): AllocationDraft => allocation.key === action.key ? { ...allocation, percentage: action.percentage } : allocation),
        error: null,
      };
    case "submit_started":
      return { ...state, isSubmitting: true, error: null };
    case "submit_failed":
      return { ...state, isSubmitting: false, error: action.error };
    case "submit_finished":
      return { ...state, isSubmitting: false };
  }
}

function responseError(result: { message?: string; data?: unknown }): string {
  const context = result.data === undefined ? "" : ` ${JSON.stringify(result.data)}`;
  return `${result.message || "Failed to update invoice allocations."}${context}`;
}

export function InvoiceAllocationsDialog({
  entry,
  customerOptions,
  onClose,
}: {
  entry: OtcInvoiceQueueListItem | null;
  customerOptions: CustomerOption[];
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [state, dispatch] = useReducer(allocationReducer, initialState);
  const updateAllocations = useUpdateOtcInvoiceAllocations();

  useEffect(() => {
    if (entry) dispatch({ type: "initialize", allocations: entry.allocations });
  }, [entry]);

  const totalPercentage = useMemo((): Decimal | null => {
    try {
      return state.allocations.reduce(
        (total: Decimal, allocation: AllocationDraft): Decimal => total.plus(allocation.percentage || 0),
        new Decimal(0),
      );
    } catch {
      return null;
    }
  }, [state.allocations]);

  const handleSubmit = async (): Promise<void> => {
    if (!entry || state.isSubmitting) return;

    const parsed = UpdateOtcInvoiceAllocationsRequestValidation.safeParse({
      queue_entry_id: entry.queue_entry_id,
      allocations: state.allocations.map((allocation: AllocationDraft) => ({
        ...(allocation.id ? { id: allocation.id } : {}),
        customer_id: allocation.customerId,
        percentage: allocation.percentage,
      })),
    });
    if (!parsed.success) {
      dispatch({ type: "submit_failed", error: parsed.error.issues.map((issue) => issue.message).join(". ") });
      return;
    }

    dispatch({ type: "submit_started" });
    try {
      const result = await updateAllocations(parsed.data);
      if (!result.success) {
        dispatch({ type: "submit_failed", error: responseError(result) });
        return;
      }
      toast({
        title: "Invoice allocations updated",
        description: `${result.data.created_count} created, ${result.data.updated_count} updated, ${result.data.deleted_count} deleted.`,
      });
      dispatch({ type: "submit_finished" });
      onClose();
    } catch {
      dispatch({ type: "submit_failed", error: "Failed to update invoice allocations." });
    }
  };

  return (
    <Dialog open={entry !== null} onOpenChange={(open: boolean) => { if (!open && !state.isSubmitting) onClose(); }}>
      <DialogContent className="sm:max-w-[760px]">
        <DialogHeader>
          <DialogTitle>Invoice Pricing Split</DialogTitle>
          <DialogDescription>
            {entry?.contract_number || "—"} / {getAmendmentTypeDisplay(entry?.amendment_type)}. Allocation percentages must total exactly 100%.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-3 overflow-y-auto py-1">
          {state.allocations.map((allocation: AllocationDraft, index: number) => (
            <div key={allocation.key} className="grid items-end gap-3 rounded-md border p-3 sm:grid-cols-[1fr_180px_auto]">
              <div className="grid gap-2">
                <Label>Customer {index + 1}</Label>
                <Combobox
                  value={allocation.customerId}
                  onValueChange={(customerId: string) => dispatch({ type: "set_customer", key: allocation.key, customerId })}
                  options={customerOptions}
                  placeholder="Select customer"
                  disabled={state.isSubmitting}
                  isModal
                  className="w-full"
                  popoverClassName="w-[360px]"
                  virtualizeList
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor={`allocation-percentage-${allocation.key}`}>Percentage</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id={`allocation-percentage-${allocation.key}`}
                    value={allocation.percentage}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => dispatch({ type: "set_percentage", key: allocation.key, percentage: event.target.value })}
                    inputMode="decimal"
                    disabled={state.isSubmitting}
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => dispatch({ type: "remove", key: allocation.key })} disabled={state.isSubmitting} aria-label={`Remove allocation ${index + 1}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => dispatch({ type: "add" })} disabled={state.isSubmitting}>
            <Plus className="mr-2 h-4 w-4" /> Add Allocation
          </Button>
        </div>
        <div className="text-sm">
          Total: <span className={totalPercentage?.eq(100) ? "font-medium text-green-700" : "font-medium text-destructive"}>{totalPercentage?.toString() ?? "Invalid"}%</span>
        </div>
        {state.error && <p className="text-sm text-destructive">{state.error}</p>}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={state.isSubmitting}>Cancel</Button>
          <Button type="button" onClick={handleSubmit} disabled={state.isSubmitting || state.allocations.length === 0 || totalPercentage?.eq(100) !== true}>
            {state.isSubmitting ? "Saving..." : "Save Allocations"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
